import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ExternalLink, Info } from "lucide-react";
import { adminGetSettings, adminUpdateSettings } from "@/lib/api";

export default function SettingsPanel() {
  const [form, setForm] = useState({
    google_adsense_publisher_id: "",
    google_adsense_slot_sidebar: "",
    google_adsense_slot_inline: "",
    amazon_associate_tag: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = await adminGetSettings();
        setForm((f) => ({
          ...f,
          google_adsense_publisher_id: s.google_adsense_publisher_id || "",
          google_adsense_slot_sidebar: s.google_adsense_slot_sidebar || "",
          google_adsense_slot_inline: s.google_adsense_slot_inline || "",
          amazon_associate_tag: s.amazon_associate_tag || "",
        }));
      } catch {
        toast.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Trim & validate publisher id format if provided
      const pub = form.google_adsense_publisher_id.trim();
      if (pub && !/^ca-pub-\d{10,20}$/i.test(pub)) {
        toast.error("Publisher ID should look like ca-pub-1234567890");
        setSaving(false);
        return;
      }
      await adminUpdateSettings({
        google_adsense_publisher_id: pub,
        google_adsense_slot_sidebar: form.google_adsense_slot_sidebar.trim(),
        google_adsense_slot_inline: form.google_adsense_slot_inline.trim(),
        amazon_associate_tag: form.amazon_associate_tag.trim(),
      });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full h-11 px-3.5 rounded-md bg-white text-sm outline-none focus:ring-2 font-mono";
  const inputStyle = { border: "1px solid var(--jaf-border)", color: "var(--jaf-text)" };
  const label = "block text-sm font-medium mb-1.5";
  const hint = "text-xs mt-1.5";

  if (loading) {
    return <div className="h-40 rounded-xl animate-pulse" style={{ backgroundColor: "var(--jaf-surface)" }} />;
  }

  return (
    <form onSubmit={onSave} data-testid="settings-panel" className="grid lg:grid-cols-2 gap-6">
      {/* AdSense */}
      <section
        className="rounded-xl p-6"
        style={{ backgroundColor: "#fff", border: "1px solid var(--jaf-border)" }}
        data-testid="settings-adsense"
      >
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2 className="font-serif text-2xl">Google AdSense</h2>
          <a
            href="https://www.google.com/adsense/start/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs inline-flex items-center gap-1 mt-1"
            style={{ color: "var(--jaf-text-muted)" }}
          >
            AdSense console <ExternalLink size={11} />
          </a>
        </div>
        <p className="text-sm mb-6" style={{ color: "var(--jaf-text-muted)" }}>
          When the publisher ID and at least one slot are set, real ads render in place of the placeholders.
        </p>

        <div className="space-y-4">
          <div>
            <label className={label}>Publisher ID</label>
            <input
              type="text"
              value={form.google_adsense_publisher_id}
              onChange={(e) => set("google_adsense_publisher_id", e.target.value)}
              placeholder="ca-pub-1234567890123456"
              data-testid="settings-publisher-id"
              className={inputCls}
              style={inputStyle}
            />
            <p className={hint} style={{ color: "var(--jaf-text-muted)" }}>
              From AdSense → Account → Account information.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={label}>Sidebar slot ID</label>
              <input
                type="text"
                value={form.google_adsense_slot_sidebar}
                onChange={(e) => set("google_adsense_slot_sidebar", e.target.value)}
                placeholder="1234567890"
                data-testid="settings-slot-sidebar"
                className={inputCls}
                style={inputStyle}
              />
              <p className={hint} style={{ color: "var(--jaf-text-muted)" }}>Used on desktop recipe pages.</p>
            </div>
            <div>
              <label className={label}>Inline slot ID</label>
              <input
                type="text"
                value={form.google_adsense_slot_inline}
                onChange={(e) => set("google_adsense_slot_inline", e.target.value)}
                placeholder="9876543210"
                data-testid="settings-slot-inline"
                className={inputCls}
                style={inputStyle}
              />
              <p className={hint} style={{ color: "var(--jaf-text-muted)" }}>Used on mobile recipe pages.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Amazon */}
      <section
        className="rounded-xl p-6"
        style={{ backgroundColor: "#fff", border: "1px solid var(--jaf-border)" }}
        data-testid="settings-amazon"
      >
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2 className="font-serif text-2xl">Amazon Associates</h2>
          <a
            href="https://affiliate-program.amazon.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs inline-flex items-center gap-1 mt-1"
            style={{ color: "var(--jaf-text-muted)" }}
          >
            Affiliate console <ExternalLink size={11} />
          </a>
        </div>
        <p className="text-sm mb-6" style={{ color: "var(--jaf-text-muted)" }}>
          Every Amazon link on the site is rewritten with this tag so qualifying purchases earn you a commission.
        </p>

        <div>
          <label className={label}>Associate tag</label>
          <input
            type="text"
            value={form.amazon_associate_tag}
            onChange={(e) => set("amazon_associate_tag", e.target.value)}
            placeholder="justairfry-20"
            data-testid="settings-amazon-tag"
            className={inputCls}
            style={inputStyle}
          />
          <p className={hint} style={{ color: "var(--jaf-text-muted)" }}>
            Looks like <code>yoursite-20</code>. Update this whenever Amazon issues a new tag.
          </p>
        </div>

        <div
          className="mt-6 rounded-md p-4 flex gap-2 text-sm items-start"
          style={{ backgroundColor: "var(--jaf-surface)", color: "var(--jaf-text-muted)" }}
        >
          <Info size={16} className="flex-shrink-0 mt-0.5" />
          <span>
            Existing recipe and resource pages already use this tag automatically — no need to touch each link.
          </span>
        </div>
      </section>

      <div className="lg:col-span-2 flex justify-end">
        <button
          type="submit"
          disabled={saving}
          data-testid="settings-save-btn"
          className="jaf-btn-primary inline-flex items-center gap-2 px-6 h-11 rounded-md text-sm font-medium disabled:opacity-60"
        >
          <Save size={15} /> {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </form>
  );
}
